import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import type { PredictionResponse } from "@/services/predictionService";

interface Props {
  prediction: PredictionResponse;
  envData?: any;
}

const PredictionComparison: React.FC<Props> = ({ prediction, envData }) => {
  const years = 5;

  const current = {
    deforestation: envData?.deforestation?.ratePerYear ?? 0, // percent per year
    landLoss: (envData?.landLoss?.ratePerYear ?? 0) * years, // 5-year total km²
    desert: (envData?.desertification?.ratePerYear ?? 0) * years, // 5-year total km²
    species: envData?.endangeredSpecies?.count ?? 0, // count
  };

  const projected = {
    deforestation: prediction?.predictions?.deforestation_rate_5y ?? 0, // percent per year
    landLoss: prediction?.predictions?.land_loss_5y ?? 0, // 5-year total km²
    desert: prediction?.predictions?.desertification_area_5y ?? 0, // 5-year total km²
    species:
      (envData?.endangeredSpecies?.count ?? 0) *
      ((prediction?.predictions?.species_impact_5y ?? 0) / 100),
  };

  const [showSpeciesInfo, setShowSpeciesInfo] = React.useState(false);

  const MetricRow: React.FC<{
    label: string;
    cur: number;
    proj: number;
    unit?: string;
    infoIcon?: boolean;
    infoContent?: React.ReactNode;
    showPercent?: boolean;
  }> = ({
    label,
    cur,
    proj,
    unit,
    infoIcon,
    infoContent,
    showPercent = true,
  }) => {
    const delta = proj - cur;
    const percent = cur !== 0 ? (delta / Math.abs(cur)) * 100 : NaN;

    const formatValue = (v: number | undefined) => {
      if (v === undefined || v === null) return "-";
      if (unit === "km²") return Number(v).toLocaleString();
      if (unit === "%") return `${Number(v).toFixed(2)}%`;
      if (unit === "species") return `${Math.round(v)}`;
      return Number(v).toFixed(2);
    };

    return (
      <div className="grid grid-cols-3 gap-4 py-2 relative items-center">
        <div className="text-xs font-medium flex items-center gap-1">
          {label}
          {infoIcon && (
            <button
              type="button"
              aria-label="Info"
              className="ml-1 text-blue-500 hover:text-blue-700 focus:outline-none"
              onClick={() => setShowSpeciesInfo((v) => !v)}
              style={{ lineHeight: 0 }}
            >
              <svg
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <text
                  x="10"
                  y="15"
                  textAnchor="middle"
                  fontSize="12"
                  fill="currentColor"
                >
                  i
                </text>
              </svg>
            </button>
          )}
        </div>
        <div className="text-xs text-muted-foreground text-center">
          Baseline vs Projection
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold">
            {formatValue(proj)}
            {unit === "km²" ? " km²" : unit === "species" ? " species" : ""}
          </div>
          <div className="text-xs text-muted-foreground">
            {showPercent && (isNaN(percent) ? "" : `${percent.toFixed(1)}%`)}
            {!showPercent && unit === "km²" && !isNaN(delta) && delta !== 0 && (
              <span>
                {delta > 0 ? "+" : ""}
                {delta.toLocaleString()} km²
              </span>
            )}
          </div>
        </div>

        {infoIcon && showSpeciesInfo && (
          <div className="absolute z-10 top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded shadow-lg p-3 text-xs text-gray-700 dark:text-gray-200">
            {infoContent}
            <button
              className="absolute top-1 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              onClick={() => setShowSpeciesInfo(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Baseline vs Projection</CardTitle>
      </CardHeader>
      <CardContent className="text-left">
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-4 pb-2 mb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="text-xs font-semibold text-muted-foreground">
            Metric
          </div>
          <div className="text-xs font-semibold text-muted-foreground text-center">
            Comparison
          </div>
          <div className="text-xs font-semibold text-muted-foreground text-right">
            Projected Value
          </div>
        </div>

        <MetricRow
          label="Forests (deforestation rate)"
          cur={current.deforestation}
          proj={projected.deforestation}
          unit="%"
          showPercent
        />
        <MetricRow
          label="Land Loss (5-year total)"
          cur={current.landLoss}
          proj={projected.landLoss}
          unit="km²"
          showPercent={false}
        />
        <MetricRow
          label="Desertification (5-year total)"
          cur={current.desert}
          proj={projected.desert}
          unit="km²"
          showPercent={false}
        />
        <MetricRow
          label="Species Impact"
          cur={current.species}
          proj={projected.species}
          unit="species"
          infoIcon
          infoContent={
            <>
              <strong>Species Impact:</strong> This projection estimates the
              number of currently endangered species in the region that may be
              further impacted (e.g., become extinct or move to a higher risk
              category) over the next 5 years, based on the model's predicted
              percentage impact. The calculation multiplies the current
              endangered species count by the projected impact percentage.
            </>
          }
          showPercent
        />
      </CardContent>
    </Card>
  );
};

export default PredictionComparison;
